import { Pause, Play, SkipBack, SkipForward, RotateCcw, Square } from "lucide-react";
import type { VoiceSyncState } from "@/hooks/useVoiceSync";

type Props = {
  syncState: VoiceSyncState;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  onSkipForward: () => void;
  onSkipBackward: () => void;
  onReplay: () => void;
};

const VoicePlaybackControls = ({
  syncState,
  onPause,
  onResume,
  onStop,
  onSkipForward,
  onSkipBackward,
  onReplay,
}: Props) => {
  const { isPlaying, isPaused, isFinished, currentSegmentIndex, segments } = syncState;

  // Show replay after finished
  if (isFinished || (!isPlaying && !isPaused && segments.length > 0)) {
    return (
      <button
        onClick={onReplay}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
        title="Réécouter"
      >
        <RotateCcw className="w-3.5 h-3.5" /> Réécouter
      </button>
    );
  }

  if (!isPlaying && !isPaused) return null;

  const progress = segments.length > 0 ? Math.round(((currentSegmentIndex + 1) / segments.length) * 100) : 0;

  return (
    <div className="flex items-center gap-1">
      {/* Voice waveform indicator */}
      {!isPaused && (
        <div className="flex items-center gap-0.5 mr-1">
          <span className="w-1 h-3 bg-primary rounded-full animate-pulse" />
          <span className="w-1 h-4 bg-primary rounded-full animate-pulse [animation-delay:150ms]" />
          <span className="w-1 h-2.5 bg-primary rounded-full animate-pulse [animation-delay:300ms]" />
          <span className="w-1 h-3.5 bg-primary rounded-full animate-pulse [animation-delay:100ms]" />
        </div>
      )}

      {/* Skip backward */}
      <button
        onClick={onSkipBackward}
        disabled={currentSegmentIndex <= 0}
        className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-30"
        title="Phrase précédente"
      >
        <SkipBack className="w-3.5 h-3.5" />
      </button>

      {/* Play/Pause */}
      {isPaused ? (
        <button
          onClick={onResume}
          className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
          title="Reprendre"
        >
          <Play className="w-4 h-4" />
        </button>
      ) : (
        <button
          onClick={onPause}
          className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
          title="Pause"
        >
          <Pause className="w-4 h-4" />
        </button>
      )}

      {/* Skip forward */}
      <button
        onClick={onSkipForward}
        disabled={currentSegmentIndex >= segments.length - 1}
        className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-30"
        title="Phrase suivante"
      >
        <SkipForward className="w-3.5 h-3.5" />
      </button>

      {/* Stop */}
      <button
        onClick={onStop}
        className="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
        title="Arrêter"
      >
        <Square className="w-3 h-3" />
      </button>

      {/* Progress */}
      <div className="flex items-center gap-1.5 ml-1">
        <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-[10px] font-bold text-muted-foreground tabular-nums">
          {currentSegmentIndex + 1}/{segments.length}
        </span>
      </div>
    </div>
  );
};

export default VoicePlaybackControls;
